/**
 * Transfer Group Template service
 */
import { Injectable, Injector } from '@angular/core';

import { BaseService } from '../base';
import {
  IEntityConfig,
  ENTITY_TYPES,
  CELL_RENDERER,
  CELL_FILTER,
  TRANSFER_TYPE,
  COLUMN_SELECTOR_ID,
  IModalDialogResult,
  ObjectId,
  IBaseEntityResponse,
  EntityId,
} from '@shared/types';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { transferTypeFormatter } from '../transfer/transfer.util';
import { BTN_REMOVE } from '@app/text-content/button-templates/button-templates-content';
import { filter, get, isEqual, map, size } from 'lodash';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { PermissionService } from '../session/permission.service';
import { lazyInject } from '@app/containers/base/decorators';
import { rx } from '@app/shared/rx';
import { removeTemplateModalOptions } from './remove-template-modal.defination';
import { batchRequestMap } from '@app/shared/rx-operators';
import { ITransferGroupNewTemplatePayload } from './transfer-group-template.types';
import { TransferGroupService } from '../transfer-group/transfer-group.service';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

const entityType: ENTITY_TYPES = ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE;
const config: IEntityConfig = {
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  buttons: null,
  apiEndpoint: entityType,
  uiRoute: entityType,
  isFormServiceSupported: true,
  list: {
    columns: [],
    documentQuery: {
      notNeedActive: true,
      orderby: 'name asc',
      filter: 'is_active eq true',
    },
  },
};

/**
 * Transfer Group Template service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class TransferGroupTemplateService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;

  @lazyInject(XVII18nService) public i18nService: XVII18nService;
  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(TransferGroupService)
  public transferGroupService: TransferGroupService;

  public quickFilterFields: string[] = ['name'];

  public listProjection: string[] = ['name'];

  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];

  public constructor(public injector: Injector) {
    super(injector);
    this.config = TransferGroupTemplateService.config;

    this.config.buttons = {
      create: {
        caption: I18n.Forms.TransferForm.NewTemplate,
        hidden: this.isHideCreateButton(),
      },
      remove: {
        ...BTN_REMOVE,
        click: this.handleTemplateItemsRemove.bind(this),
      },
    };

    this.config.list.columns = [
      {
        path: 'name',
        title: I18n.Forms.TransferForm.TemplateName,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        cellRenderer: CELL_RENDERER.entity_link_renderer,
        defaultView: true,
      },
      {
        path: 'transfer_group_name',
        title: I18n.Forms.TransferForm.TransferGroupName,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        cellRenderer: CELL_RENDERER.entity_link_renderer,
        defaultView: true,
      },
      {
        path: 'site_name',
        title: I18n.Forms.TransferForm.FromNameBrackets,
        filterType: 'stringFilter',
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'store_number',
        title: I18n.Forms.TransferForm.FromIDBrackets,
        filterType: 'stringFilter',
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'transfer_type',
        title: I18n.Common.Type,
        filterType: CELL_FILTER.enum,
        filterParams: {
          values: [
            {
              text: I18n.Forms.TransferForm.PreEventHyphen,
              value: TRANSFER_TYPE.PRE_EVENT_TRANSFER,
            },
            {
              text: I18n.Forms.TransferForm.DuringEvent,
              value: TRANSFER_TYPE.DURING_EVENT_TRANSFER,
            },
            {
              text: I18n.Forms.TransferForm.OutsideofEvent,
              value: TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
            },
          ],
        },
        sortable: true,
        valueFormatter: (params) =>
          transferTypeFormatter(params, { i18nService: this.i18nService }),
        defaultView: true,
      },
      {
        path: COLUMN_SELECTOR_ID,
        width: 50,
        title: '',
        defaultView: true,
        cellRenderer: CELL_RENDERER.transfer_group_template_actions_renderer,
        pinned: detectMobile() ? false : 'right',
        lockPinned: true,
      },
    ];

    if (
      !this.permissionService.canManage(entityType) &&
      !this.permissionService.canDelete(entityType)
    ) {
      this.config.list.columns = filter(
        config.list.columns,
        (column) => !isEqual(column.path, COLUMN_SELECTOR_ID),
      );
    }
  }

  public async handleTemplateItemsRemove(removeItems): Promise<boolean> {
    const templateIds = map(removeItems, '_id');

    const { confirmed }: IModalDialogResult =
      await this.uiActions.promptFormModal(
        removeTemplateModalOptions({
          isMultiSelect: size(templateIds) > 1,
        }),
      );
    if (!confirmed) {
      return false;
    }

    await batchRequestMap(templateIds, (templateId: ObjectId) =>
      this.deleteItems(templateId, {}),
    )
      .pipe(
        rx.reduce(
          (totalResult) => {
            totalResult.total++;

            return totalResult;
          },
          <IBaseEntityResponse>{ items: [], total: 0 },
        ),
      )
      .toPromise();

    return true;
  }

  public isHideCreateButton(): boolean {
    return !this.permissionService.canAdd(entityType);
  }

  public async getNewTemplatePayload(
    templateName: string,
    transferGroupId: EntityId,
  ): Promise<ITransferGroupNewTemplatePayload> {
    const groupDocument =
      await this.transferGroupService.getDocumentByIdWithSkipUpdateDocument(
        transferGroupId,
        {
          query: {
            transfer_fields_select: 'site_id_to',
            include_transfer: true,
          },
          noLoader: true,
        },
      );

    return {
      name: templateName,
      site_id: get(groupDocument, 'site_id'),
      transfer_type: get(groupDocument, 'transfer_type'),
      transfer_group_name: get(groupDocument, 'name'),
      site_ids_to: map(
        get(groupDocument, ['virtual', 'transfer_items'], []),
        'site_id_to',
      ),
    };
  }
}
